/**
 * FieldMappingEditor - Visual Field Mapping Interface
 * 
 * Provides drag-and-drop interface for mapping fields between data sources
 * Integrates with Mappr for field mapping logic
 * 
 * CHANGE HISTORY:
 * - 251107_2345 [pending] danielbeer: Created visual field mapping editor
 */

import React, { useState } from 'react';
import './FieldMappingEditor.css';

export interface FieldMapping {
  id: string;
  sourceField: string;
  targetField: string;
  transform?: string;
  required?: boolean;
}

export interface DataSource {
  id: string;
  name: string;
  fields: SourceField[];
}

export interface SourceField {
  name: string;
  type: string;
  sample?: any;
}

export interface TargetField {
  name: string;
  type: string;
  required?: boolean;
}

interface FieldMappingEditorProps {
  source: DataSource;
  targetFields: TargetField[];
  mappings: FieldMapping[];
  onMappingsChange: (mappings: FieldMapping[]) => void;
}

export function FieldMappingEditor({
  source,
  targetFields,
  mappings,
  onMappingsChange
}: FieldMappingEditorProps) {
  const [draggedField, setDraggedField] = useState<string | null>(null);
  const [selectedMapping, setSelectedMapping] = useState<string | null>(null);

  const handleDragStart = (fieldName: string) => {
    setDraggedField(fieldName);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetFieldName: string) => {
    if (!draggedField) return;

    // Check if mapping already exists
    const existingMapping = mappings.find(m => m.targetField === targetFieldName);
    
    if (existingMapping) {
      // Update existing mapping
      const updated = mappings.map(m =>
        m.targetField === targetFieldName
          ? { ...m, sourceField: draggedField }
          : m
      );
      onMappingsChange(updated);
    } else {
      // Create new mapping
      const newMapping: FieldMapping = {
        id: `mapping-${Date.now()}`,
        sourceField: draggedField,
        targetField: targetFieldName,
        required: targetFields.find(f => f.name === targetFieldName)?.required
      };
      onMappingsChange([...mappings, newMapping]);
    }

    setDraggedField(null);
  };

  const handleRemoveMapping = (mappingId: string) => {
    onMappingsChange(mappings.filter(m => m.id !== mappingId));
  };

  const handleAutoMap = () => {
    // Auto-map fields with matching names
    const autoMappings: FieldMapping[] = [];
    
    targetFields.forEach(targetField => {
      const matchingSource = source.fields.find(
        sf => sf.name.toLowerCase() === targetField.name.toLowerCase()
      );
      
      if (matchingSource && !mappings.find(m => m.targetField === targetField.name)) {
        autoMappings.push({
          id: `mapping-${Date.now()}-${targetField.name}`,
          sourceField: matchingSource.name,
          targetField: targetField.name,
          required: targetField.required
        });
      }
    });

    onMappingsChange([...mappings, ...autoMappings]);
  };

  const getMappingForTarget = (targetFieldName: string) => {
    return mappings.find(m => m.targetField === targetFieldName);
  };

  const getFieldTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'string': return '📝';
      case 'number': return '🔢';
      case 'boolean': return '✓';
      case 'date': return '📅';
      case 'array': return '📋';
      case 'object': return '📦';
      default: return '•';
    }
  };

  return (
    <div className="field-mapping-editor">
      <div className="mapping-header">
        <h3>Field Mapping</h3>
        <button className="auto-map-button" onClick={handleAutoMap}>
          ⚡ Auto-Map
        </button>
      </div>

      <div className="mapping-container">
        {/* Source Fields */}
        <div className="source-panel">
          <div className="panel-header">
            <h4>Source: {source.name}</h4>
            <span className="field-count">{source.fields.length} fields</span>
          </div>
          <div className="field-list">
            {source.fields.map(field => (
              <div
                key={field.name}
                className="source-field"
                draggable
                onDragStart={() => handleDragStart(field.name)}
              >
                <span className="field-icon">{getFieldTypeIcon(field.type)}</span>
                <div className="field-info">
                  <div className="field-name">{field.name}</div>
                  <div className="field-type">{field.type}</div>
                </div>
                {field.sample && (
                  <div className="field-sample" title={String(field.sample)}>
                    {String(field.sample).substring(0, 20)}
                    {String(field.sample).length > 20 ? '...' : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Mapping Connections */}
        <div className="mapping-connections">
          <svg className="connection-lines">
            {mappings.map(mapping => {
              const sourceIndex = source.fields.findIndex(f => f.name === mapping.sourceField);
              const targetIndex = targetFields.findIndex(f => f.name === mapping.targetField);
              
              if (sourceIndex === -1 || targetIndex === -1) return null;
              
              const y1 = (sourceIndex * 60) + 30;
              const y2 = (targetIndex * 60) + 30;
              
              return (
                <line
                  key={mapping.id}
                  x1="0"
                  y1={y1}
                  x2="100%"
                  y2={y2}
                  stroke={selectedMapping === mapping.id ? '#667eea' : '#cbd5e0'}
                  strokeWidth={selectedMapping === mapping.id ? '3' : '2'}
                  onClick={() => setSelectedMapping(mapping.id)}
                  style={{ cursor: 'pointer' }}
                />
              );
            })}
          </svg>
        </div>

        {/* Target Fields */}
        <div className="target-panel">
          <div className="panel-header">
            <h4>Target Dataset</h4>
            <span className="field-count">{targetFields.length} fields</span>
          </div>
          <div className="field-list">
            {targetFields.map(field => {
              const mapping = getMappingForTarget(field.name);
              return (
                <div
                  key={field.name}
                  className={`target-field ${mapping ? 'mapped' : ''} ${field.required ? 'required' : ''}`}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(field.name)}
                >
                  <span className="field-icon">{getFieldTypeIcon(field.type)}</span>
                  <div className="field-info">
                    <div className="field-name">
                      {field.name}
                      {field.required && <span className="required-indicator">*</span>}
                    </div>
                    <div className="field-type">{field.type}</div>
                    {mapping && (
                      <div className="mapped-from">
                        ← {mapping.sourceField}
                        <button
                          className="remove-mapping"
                          onClick={() => handleRemoveMapping(mapping.id)}
                          title="Remove mapping"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mapping Stats */}
      <div className="mapping-stats">
        <div className="stat">
          <span className="stat-label">Mapped:</span>
          <span className="stat-value">{mappings.length} / {targetFields.length}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Required:</span>
          <span className="stat-value">
            {mappings.filter(m => m.required).length} / {targetFields.filter(f => f.required).length}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Unmapped:</span>
          <span className="stat-value warning">
            {targetFields.length - mappings.length}
          </span>
        </div>
      </div>
    </div>
  );
}
